/**
 * Sipinor school admin colour palettes + fonts.
 * Applied as CSS custom properties on <html>. Persisted in localStorage.
 */

export const STORAGE_KEY = 'sipinor_school_theme'
export const DEFAULT_THEME_ID = 'cdss-navy'
export const DEFAULT_FONT_ID = 'roboto'

const TOKEN_KEYS = [
    ['--sip-sidebar', 'sidebar'],
    ['--sip-sidebar-text', 'sidebarText'],
    ['--sip-sidebar-muted', 'sidebarMuted'],
    ['--sip-accent', 'accent'],
    ['--sip-accent-hover', 'accentHover'],
    ['--sip-accent-on', 'accentOn'],
    ['--sip-header-bg', 'headerBg'],
    ['--sip-text', 'text'],
    ['--sip-muted', 'muted'],
    ['--sip-line', 'line'],
    ['--sip-bg', 'bg'],
    ['--sip-surface', 'surface'],
    ['--sip-navy', 'navy'],
    ['--sip-rx', 'rx'],
]

function hexToRgb(hex) {
    hex = String(hex || '').replace('#', '')
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    if (hex.length !== 6) return [0, 0, 0]
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)]
}

function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (n) {
        n = Math.max(0, Math.min(255, Math.round(n)))
        return n.toString(16).padStart(2, '0')
    }).join('')
}

function mix(a, b, t) {
    var A = hexToRgb(a)
    var B = hexToRgb(b)
    return rgbToHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t)
}

function lighten(h, t) { return mix(h, '#ffffff', t) }
function darken(h, t) { return mix(h, '#000000', t) }

function luminance(hex) {
    var c = hexToRgb(hex).map(function (v) {
        v /= 255
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
}

var LIGHT_SIDEBAR = '#F8FAFC'
var LIGHT_SIDEBAR_TEXT = '#475569'
var LIGHT_SIDEBAR_MUTED = '#64748B'
var LIGHT_LINE = '#e6ecee'
var DARK_SIDEBAR = '#0a1012'

function complete(t, lightRail) {
    t = Object.assign({}, t)
    t.accentHover = t.accentHover || darken(t.accent, 0.16)
    t.accentOn = t.accentOn || (lightRail ? t.accent : lighten(t.accent, 0.42))
    t.navy = t.navy || (lightRail ? '#0c262c' : lighten(t.sidebar, 0.1))
    t.rx = t.rx || mix(t.accent, '#2f6f8f', 0.45)
    t.surface = t.surface || '#ffffff'
    t.headerBg = t.headerBg || (lightRail ? '#ffffff' : t.surface)
    t.text = t.text || '#0c262c'
    t.muted = t.muted || '#5d7176'
    t.line = t.line || (lightRail ? LIGHT_LINE : '#e3ecee')
    t.bg = t.bg || '#f4f6f8'
    t.sidebarText = t.sidebarText || (lightRail ? LIGHT_SIDEBAR_TEXT : 'rgba(255,255,255,0.82)')
    t.sidebarMuted = t.sidebarMuted || (lightRail ? LIGHT_SIDEBAR_MUTED : 'rgba(255,255,255,0.38)')
    return t
}

function darkFrom(light, over) {
    return complete(Object.assign({
        sidebar: DARK_SIDEBAR,
        accent: light.accent,
        accentHover: light.accentHover || darken(light.accent, 0.16),
        accentOn: lighten(light.accent, 0.42),
        navy: mix('#dce8ea', light.accent, 0.12),
        rx: light.rx || light.accent,
        text: '#e8eef0',
        muted: '#9aa8ad',
        line: '#243033',
        bg: '#0e1214',
        surface: '#1a2224',
        headerBg: '#1a2224',
        sidebarText: 'rgba(255,255,255,0.84)',
        sidebarMuted: 'rgba(255,255,255,0.4)',
    }, over || {}, { sidebar: DARK_SIDEBAR, bg: '#0e1214' }), false)
}

function theme(id, name, blurb, light, darkOver, extra) {
    extra = extra || {}
    light = Object.assign({}, light, {
        sidebar: LIGHT_SIDEBAR,
        sidebarText: LIGHT_SIDEBAR_TEXT,
        sidebarMuted: LIGHT_SIDEBAR_MUTED,
        headerBg: '#ffffff',
        surface: '#ffffff',
        bg: '#f4f6f8',
        line: light.line || LIGHT_LINE,
        text: light.text || '#0c262c',
    })
    var L = complete(light, true)
    var D = darkFrom(L, darkOver)
    D.bg = '#0e1214'
    D.sidebar = DARK_SIDEBAR
    return {
        id: id,
        name: name,
        blurb: blurb,
        lightSidebar: true,
        swatches: [LIGHT_SIDEBAR, L.accent, L.accentHover],
        light: L,
        dark: D,
    }
}

export const SCHOOL_THEMES = [
    theme('cdss-navy', 'CDSS Slate Navy', 'Deep institutional navy with clinical cyan accent (MedEazy CDSS)', {
        sidebar: '#F8FAFC', accent: '#0284C7', accentHover: '#0369A1', accentOn: '#0284C7',
        navy: '#0A1128', rx: '#0EA5E9', text: '#0F172A', muted: '#64748B',
        line: '#E2E8F0', bg: '#F8FAFC', surface: '#ffffff',
        sidebarText: '#475569', sidebarMuted: '#64748B',
    }, {
        sidebar: '#080E21', accent: '#38BDF8', accentHover: '#7DD3FC',
        bg: '#0A1128', surface: '#1E293B', line: '#334155',
    }),
    theme('forest-leaf', 'Forest Leaf', 'Teal forest — classic school look', {
        sidebar: '#0c262c', accent: '#0f7a62', accentHover: '#0b5e4b', accentOn: '#7dcfb8',
        navy: '#12363d', rx: '#2f6f8f', text: '#1a2e32', muted: '#5d7176',
        line: '#e3ecee', bg: '#f0f5f5', surface: '#ffffff',
    }, {
        sidebar: '#081618', accent: '#2bb890', accentHover: '#7dcfb8',
        bg: '#0a1c21', surface: '#122a30', line: '#2a4046',
    }),
    theme('deep-navy', 'Deep Navy', 'Trust-first navy with a cobalt accent', {
        sidebar: '#0a1628', accent: '#1e4d8c', accentHover: '#163a6b', accentOn: '#8fb4e8',
        navy: '#12203a', rx: '#3d7ea6', text: '#152536', muted: '#5a6b78',
        line: '#dce6ee', bg: '#f3f6fa', surface: '#ffffff',
    }),
    theme('midnight-ink', 'Midnight Ink', 'Near-black chrome, electric indigo', {
        sidebar: '#0b0c12', accent: '#6366f1', accentHover: '#4f46e5', accentOn: '#c4b5fd',
        navy: '#16182a', rx: '#818cf8', text: '#1e1b2e', muted: '#64607a',
        line: '#e4e2f0', bg: '#f5f4fb', surface: '#ffffff',
    }),
    theme('slate-steel', 'Slate Steel', 'Cool blue-gray millwork', {
        sidebar: '#2a333c', accent: '#5b7c99', accentHover: '#456178', accentOn: '#b7c9d8',
        navy: '#343e48', rx: '#6a8899', text: '#22282e', muted: '#66707a',
        line: '#e2e6ea', bg: '#f4f5f7', surface: '#ffffff',
    }),
    theme('graphite', 'Graphite', 'Charcoal rail with silver hardware', {
        sidebar: '#1c1f24', accent: '#6b7280', accentHover: '#4b5563', accentOn: '#d1d5db',
        navy: '#272b32', rx: '#94a3b8', text: '#1f2328', muted: '#6b7280',
        line: '#e5e7eb', bg: '#f4f5f6', surface: '#ffffff',
    }),
    theme('sandstone', 'Sandstone', 'Warm desert stone and ochre', {
        sidebar: '#3d3428', accent: '#c4a574', accentHover: '#a88858', accentOn: '#ead9b8',
        navy: '#4a4032', rx: '#8a7a62', text: '#2c261e', muted: '#7a6e5e',
        line: '#ebe4d8', bg: '#f7f3ec', surface: '#fffcf7',
    }),
    theme('terracotta', 'Terracotta', 'Clay kiln orange on umber', {
        sidebar: '#3a2218', accent: '#c45c26', accentHover: '#9a461c', accentOn: '#f0b090',
        navy: '#4a2c20', rx: '#b07a4a', text: '#2c1c16', muted: '#7a5a4e',
        line: '#f0e2d8', bg: '#faf4f0', surface: '#ffffff',
    }),
    theme('wine', 'Wine', 'Burgundy cellar, deep and formal', {
        sidebar: '#2a1218', accent: '#8b2942', accentHover: '#6c1e32', accentOn: '#e8a0b2',
        navy: '#3a1c24', rx: '#9a5a6a', text: '#2a161c', muted: '#7a5460',
        line: '#f0e0e4', bg: '#faf4f5', surface: '#ffffff',
    }),
    theme('royal-indigo', 'Royal Indigo', 'Court purple with amethyst', {
        sidebar: '#1a1440', accent: '#6d28d9', accentHover: '#5b21b6', accentOn: '#d8b4fe',
        navy: '#241c52', rx: '#7c5cbf', text: '#1e1638', muted: '#6a5e88',
        line: '#e6e0f4', bg: '#f6f3fb', surface: '#ffffff',
    }),
    theme('ocean', 'Ocean', 'Deep sea teal and aqua', {
        sidebar: '#062a3c', accent: '#0e7490', accentHover: '#0b5c72', accentOn: '#7dd3e8',
        navy: '#0c384c', rx: '#2a8aaa', text: '#123038', muted: '#547088',
        line: '#d8ecee', bg: '#f0f7f8', surface: '#ffffff',
    }),
    theme('sky', 'Sky', 'Open-air blue, bright and clear', {
        sidebar: '#1e3a5f', accent: '#0284c7', accentHover: '#0369a1', accentOn: '#7dd3fc',
        navy: '#284a72', rx: '#38bdf8', text: '#163048', muted: '#5a7890',
        line: '#d8e8f4', bg: '#f2f7fb', surface: '#ffffff',
    }),
    theme('mint-clinic', 'Mint Clinic', 'Cool clinic mint, clean and precise', {
        sidebar: '#10292d', accent: '#1a9b7c', accentHover: '#147a62', accentOn: '#8fdfcc',
        navy: '#1a3d42', rx: '#3a7d8c', text: '#1a3236', muted: '#5a7276',
        line: '#dcecea', bg: '#f3faf7', surface: '#ffffff',
    }),
    theme('emerald', 'Emerald', 'True leafy green — no teal', {
        sidebar: '#0c2818', accent: '#059669', accentHover: '#047857', accentOn: '#6ee7b7',
        navy: '#163824', rx: '#3d8a6a', text: '#14281c', muted: '#547864',
        line: '#dcebe2', bg: '#f2f8f4', surface: '#ffffff',
    }),
    theme('olive', 'Olive', 'Herb-garden drab and gold-green', {
        sidebar: '#2c2e18', accent: '#6b7c3a', accentHover: '#55622e', accentOn: '#c5d48a',
        navy: '#3a3c22', rx: '#7a8a52', text: '#28281a', muted: '#6e7058',
        line: '#e6e8d8', bg: '#f6f6ee', surface: '#ffffff',
    }),
    theme('charcoal-gold', 'Charcoal Gold', 'Evening black with brass inlay', {
        sidebar: '#161412', accent: '#c9a227', accentHover: '#a3841c', accentOn: '#f0d78a',
        navy: '#24201c', rx: '#b08a3a', text: '#221c14', muted: '#7a6e52',
        line: '#ece6d4', bg: '#f7f4ea', surface: '#fffcf4',
    }),
    theme('high-contrast', 'High Contrast', 'Black rail, yellow signal, max punch', {
        sidebar: '#000000', accent: '#eab308', accentHover: '#ca8a04', accentOn: '#fde047',
        navy: '#111111', rx: '#facc15', text: '#0a0a0a', muted: '#525252',
        line: '#d4d4d4', bg: '#fafafa', surface: '#ffffff',
    }),
    theme('soft-cloud', 'Soft Cloud', 'Light rail — airy slate and sky blue', {
        sidebar: '#e8eef4', accent: '#2563eb', accentHover: '#1d4ed8', accentOn: '#2563eb',
        navy: '#1e3a5f', rx: '#3b82f6', text: '#1e293b', muted: '#64748b',
        line: '#d5dee8', bg: '#f4f7fb', surface: '#ffffff',
        sidebarText: '#1e293b', sidebarMuted: '#64748b',
    }, {
        sidebar: '#0f1724', accent: '#60a5fa',
    }, { lightSidebar: true }),
    theme('paper', 'Paper', 'Gallery cream rail, ink-brown accent', {
        sidebar: '#f3eee4', accent: '#9a3412', accentHover: '#7c2d12', accentOn: '#9a3412',
        navy: '#3f2e22', rx: '#b45309', text: '#292524', muted: '#78716c',
        line: '#e7e0d4', bg: '#faf7f2', surface: '#fffcf8',
        sidebarText: '#292524', sidebarMuted: '#78716c',
    }, {
        sidebar: '#1c1612', accent: '#fb923c',
    }, { lightSidebar: true }),
    theme('aurora', 'Aurora', 'Twilight purple with polar cyan', {
        sidebar: '#1a1030', accent: '#06b6d4', accentHover: '#0891b2', accentOn: '#67e8f9',
        navy: '#261848', rx: '#a78bfa', text: '#1c1638', muted: '#6b6490',
        line: '#e4e0f4', bg: '#f5f3fb', surface: '#ffffff',
    }),
    theme('copper', 'Copper', 'Workshop copper on soot', {
        sidebar: '#1e1610', accent: '#b87333', accentHover: '#935c28', accentOn: '#e8c090',
        navy: '#2c2418', rx: '#c4944a', text: '#241c14', muted: '#7a6c58',
        line: '#e8e0d4', bg: '#f6f2ec', surface: '#ffffff',
    }),
    theme('blush', 'Blush', 'Dusty rose, salon-warm', {
        sidebar: '#3a2028', accent: '#db7093', accentHover: '#be4d73', accentOn: '#f8c0d2',
        navy: '#4a2c36', rx: '#c07088', text: '#2c1820', muted: '#7a5864',
        line: '#f0e0e6', bg: '#faf4f6', surface: '#ffffff',
    }),
    theme('teal-tabler', 'Teal Tabler', 'Classic Tabler blue on navy', {
        sidebar: '#1c232f', accent: '#206bc4', accentHover: '#1a579e', accentOn: '#8bb8ea',
        navy: '#252d3c', rx: '#4299e1', text: '#1a2433', muted: '#5c6b7a',
        line: '#dce4ee', bg: '#f4f6f9', surface: '#ffffff',
    }),
    theme('monochrome', 'Monochrome', 'Ink, paper, and mid-gray only', {
        sidebar: '#111111', accent: '#404040', accentHover: '#262626', accentOn: '#d4d4d4',
        navy: '#1f1f1f', rx: '#737373', text: '#171717', muted: '#737373',
        line: '#e5e5e5', bg: '#f5f5f5', surface: '#ffffff',
    }),
    theme('sakura', 'Sakura', 'Cherry blossom on dusk plum', {
        sidebar: '#3d2430', accent: '#e85d8c', accentHover: '#c73e6e', accentOn: '#f8b4cc',
        navy: '#4e3040', rx: '#d48aa8', text: '#2c1822', muted: '#7a5868',
        line: '#f0e0e8', bg: '#fbf4f7', surface: '#ffffff',
    }),
    theme('arctic', 'Arctic', 'Ice-white rail, glacier blue', {
        sidebar: '#e8f4fb', accent: '#0284c7', accentHover: '#0369a1', accentOn: '#0284c7',
        navy: '#0e3a52', rx: '#38bdf8', text: '#0f2940', muted: '#5b7a90',
        line: '#d0e4f0', bg: '#f3f8fc', surface: '#ffffff',
        sidebarText: '#0f2940', sidebarMuted: '#5b7a90',
    }, {
        sidebar: '#082028', accent: '#38bdf8',
    }, { lightSidebar: true }),
    theme('espresso', 'Espresso', 'Cafe counter brown and cream', {
        sidebar: '#1c1410', accent: '#7c4a2d', accentHover: '#5c361f', accentOn: '#e0b892',
        navy: '#2a1e18', rx: '#a07048', text: '#241c16', muted: '#746258',
        line: '#e8e0d8', bg: '#f7f3ee', surface: '#fffcf8',
    }),
]

export const CUSTOM_THEME = {
    id: 'custom',
    name: 'Custom',
    blurb: 'Pick your own accent — white rail in light mode',
    lightSidebar: true,
    swatches: ['#ffffff', '#0f7a62', '#0b5e4b'],
    isCustom: true,
}

export const SCHOOL_FONTS = [
    { id: 'roboto', name: 'Roboto (Google)', stack: 'Roboto, "Helvetica Neue", Arial, sans-serif', href: 'https://fonts.bunny.net/css?family=roboto:400,500,700&display=swap', preview: 'Clean Google Material look — like Sheets & Docs' },
    { id: 'open-sans', name: 'Open Sans', stack: '"Open Sans", Arial, sans-serif', href: 'https://fonts.bunny.net/css?family=open-sans:400,500,600,700&display=swap', preview: 'Friendly Google-style sans used across Workspace' },
    { id: 'plus-jakarta', name: 'Plus Jakarta Sans', stack: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif', href: 'https://fonts.bunny.net/css?family=plus-jakarta-sans:400,500,600,700,800|outfit:500,600,700,800&display=swap', preview: 'Institutional modern typography' },
    { id: 'outfit', name: 'Outfit', stack: '"Outfit", "Plus Jakarta Sans", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap', preview: 'Contemporary geometric display' },
    { id: 'figtree', name: 'Figtree', stack: '"Figtree", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'inter', name: 'Inter', stack: '"Inter", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'source-sans', name: 'Source Sans 3', stack: '"Source Sans 3", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700;800&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'ibm-plex', name: 'IBM Plex Sans', stack: '"IBM Plex Sans", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'dm-sans', name: 'DM Sans', stack: '"DM Sans", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'manrope', name: 'Manrope', stack: '"Manrope", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'nunito-sans', name: 'Nunito Sans', stack: '"Nunito Sans", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;500;600;700;800&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'literata', name: 'Literata', stack: '"Literata", Georgia, serif', href: 'https://fonts.googleapis.com/css2?family=Literata:wght@400;500;600;700&display=swap', preview: 'Students, staff, and classrooms in one place.' },
    { id: 'space-grotesk', name: 'Space Grotesk', stack: '"Space Grotesk", "Segoe UI", sans-serif', href: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap', preview: 'Students, staff, and classrooms in one place.' },
]

export const DEFAULT_CUSTOM = { accent: '#0f7a62', sidebar: '#ffffff' }

export function getThemeById(id) {
    for (var i = 0; i < SCHOOL_THEMES.length; i++) {
        if (SCHOOL_THEMES[i].id === id) return SCHOOL_THEMES[i]
    }
    if (id === 'custom') return CUSTOM_THEME
    return SCHOOL_THEMES[0]
}

export function getFontById(id) {
    id = normalizeFontId(id)
    for (var i = 0; i < SCHOOL_FONTS.length; i++) {
        if (SCHOOL_FONTS[i].id === id) return SCHOOL_FONTS[i]
    }
    return SCHOOL_FONTS[0]
}

/** Move previous default (Plus Jakarta) to Google-style Roboto. */
function normalizeFontId(id) {
    if (!id || id === 'plus-jakarta' || id === 'outfit') return DEFAULT_FONT_ID
    return id
}

function tokensFromCustom(custom, isDark) {
    var accent = (custom && custom.accent) || DEFAULT_CUSTOM.accent
    var light = complete({
        sidebar: LIGHT_SIDEBAR,
        accent: accent,
        navy: '#0c262c',
        rx: mix(accent, '#2f6f8f', 0.4),
        text: '#0c262c',
        muted: '#5d7176',
        line: LIGHT_LINE,
        bg: '#f4f6f8',
        surface: '#ffffff',
        headerBg: '#ffffff',
        sidebarText: LIGHT_SIDEBAR_TEXT,
        sidebarMuted: LIGHT_SIDEBAR_MUTED,
    }, true)
    if (!isDark) return light
    return darkFrom(light)
}

function collectSnapshot(tokens, font) {
    var out = {}
    TOKEN_KEYS.forEach(function (pair) {
        if (tokens[pair[1]]) out[pair[0]] = tokens[pair[1]]
    })
    out['--sip-navy-deep'] = luminance(tokens.sidebar) > 0.4
        ? (tokens.navy && luminance(tokens.navy) < 0.4 ? tokens.navy : '#0c262c')
        : tokens.sidebar
    out['--sip-brand'] = tokens.accent
    out['--sip-brand-deep'] = tokens.accentHover
    out['--sip-ink'] = tokens.text
    out['--sip-sky'] = tokens.accent
    out['--sip-cyan'] = tokens.rx || tokens.accent
    out['--sip-rx'] = tokens.rx || tokens.accent
    var rgb = hexToRgb(tokens.accent)
    var rgbStr = rgb[0] + ', ' + rgb[1] + ', ' + rgb[2]
    out['--sip-accent-rgb'] = rgbStr
    out['--sip-accent-soft'] = 'rgba(' + rgbStr + ', 0.12)'
    out['--sip-accent-soft-strong'] = 'rgba(' + rgbStr + ', 0.22)'
    out['--sip-focus-ring'] = '0 0 0 3px rgba(' + rgbStr + ', 0.2)'
    out['--sip-sidebar-active'] = tokens.accent
    out['--sip-sidebar-active-bg'] = out['--sip-accent-soft']
    out['--sip-accent-fg'] = luminance(tokens.accent) > 0.55 ? '#111111' : '#ffffff'
    out['--sip-font'] = (font && font.stack) ? font.stack : 'Roboto, "Helvetica Neue", Arial, sans-serif'
    out['--sip-font-display'] = (font && font.stack) ? font.stack : 'Roboto, "Helvetica Neue", Arial, sans-serif'
    out['--sip-heading-font'] = (font && font.stack) ? font.stack : 'Roboto, "Helvetica Neue", Arial, sans-serif'
    return out
}

function authorizeSurfaces(snap, dark) {
    if (dark) {
        snap['--sip-bg'] = '#0e1214'
        snap['--sip-sidebar'] = DARK_SIDEBAR
    } else {
        snap['--sip-bg'] = '#f4f6f8'
        snap['--sip-surface'] = '#ffffff'
        snap['--sip-sidebar'] = LIGHT_SIDEBAR
        snap['--sip-header-bg'] = '#ffffff'
        snap['--sip-sidebar-text'] = LIGHT_SIDEBAR_TEXT
        snap['--sip-sidebar-muted'] = LIGHT_SIDEBAR_MUTED
    }
    return snap
}

function writeTokens(tokens, font, dark) {
    var root = document.documentElement
    var snap = authorizeSurfaces(collectSnapshot(tokens, font), dark)
    Object.keys(snap).forEach(function (k) {
        root.style.setProperty(k, snap[k])
    })
    return snap
}

export function ensureFontLink(href) {
    var existing = document.getElementById('sip-font-link')
    if (!href) {
        if (existing) existing.parentNode.removeChild(existing)
        return
    }
    if (existing && existing.getAttribute('href') === href) return
    if (existing) existing.parentNode.removeChild(existing)
    var link = document.createElement('link')
    link.id = 'sip-font-link'
    link.rel = 'stylesheet'
    link.href = href
    document.head.appendChild(link)
}

export function isDarkMode() {
    return !!(document.body && document.body.classList.contains('dark-mode'))
        || document.documentElement.classList.contains('dark-mode')
}

function schoolBootPrefs() {
    try {
        var school = typeof window !== 'undefined' ? window.__SIP_SCHOOL_THEME__ : null
        if (school && school.themeId) {
            return {
                themeId: school.themeId,
                fontId: normalizeFontId(school.fontId || DEFAULT_FONT_ID),
                custom: Object.assign({}, DEFAULT_CUSTOM, school.custom || {}),
            }
        }
    } catch (e) {}
    return { themeId: DEFAULT_THEME_ID, fontId: DEFAULT_FONT_ID, custom: Object.assign({}, DEFAULT_CUSTOM) }
}

export function readStoredPreference() {
    try {
        var raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            var parsed = JSON.parse(raw)
            return {
                themeId: parsed.themeId || DEFAULT_THEME_ID,
                fontId: normalizeFontId(parsed.fontId || DEFAULT_FONT_ID),
                custom: Object.assign({}, DEFAULT_CUSTOM, parsed.custom || {}),
            }
        }
    } catch (e) {}
    return schoolBootPrefs()
}

export function applySchoolAppearance(prefs, force) {
    prefs = prefs || readStoredPreference()
    var themeId = prefs.themeId || DEFAULT_THEME_ID
    var fontId = normalizeFontId(prefs.fontId || DEFAULT_FONT_ID)
    var custom = Object.assign({}, DEFAULT_CUSTOM, prefs.custom || {})
    var font = getFontById(fontId)
    var dark = isDarkMode()
    var tokens
    var def = getThemeById(themeId)
    var root = document.documentElement
    var darkKey = dark ? '1' : '0'
    if (!force && root.getAttribute('data-sip-applied') === '1'
        && root.getAttribute('data-sip-theme') === themeId
        && root.getAttribute('data-sip-font') === fontId
        && root.getAttribute('data-sip-dark') === darkKey) {
        ensureFontLink(font.href)
        return prefs
    }
    if (themeId === 'custom') {
        tokens = tokensFromCustom(custom, dark)
    } else {
        tokens = dark ? def.dark : def.light
    }
    var snap = writeTokens(tokens, font, dark)
    root.setAttribute('data-sip-theme', themeId)
    root.setAttribute('data-sip-font', fontId)
    root.setAttribute('data-sip-applied', '1')
    root.setAttribute('data-sip-dark', darkKey)
    root.setAttribute('data-sip-sidebar', dark ? 'dark' : 'light')
    ensureFontLink(font.href)
    var payload = {
        themeId: themeId,
        fontId: fontId,
        custom: custom,
        tokens: snap,
        darkTokens: authorizeSurfaces(themeId === 'custom' ? collectSnapshot(tokensFromCustom(custom, true), font) : collectSnapshot(def.dark, font), true),
        lightTokens: authorizeSurfaces(themeId === 'custom' ? collectSnapshot(tokensFromCustom(custom, false), font) : collectSnapshot(def.light, font), false),
        fontFamily: font.stack,
        fontHref: font.href || '',
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {}
    return payload
}

var persistTimer = null

export function persistSchoolTheme(payload) {
    if (!payload || typeof window === 'undefined') return
    var axios = window.axios
    if (!axios) return
    var helper = window.helper
    if (helper && helper.hasPermission && !helper.hasPermission('access-configuration')) return
    clearTimeout(persistTimer)
    persistTimer = setTimeout(function () {
        var accent = (payload.tokens && payload.tokens['--sip-accent'])
            || (payload.custom && payload.custom.accent)
            || DEFAULT_CUSTOM.accent
        var sidebar = (payload.custom && payload.custom.sidebar) || LIGHT_SIDEBAR
        axios.post('/api/configuration', {
            config_type: 'ui_theme',
            ui_theme: payload.themeId || DEFAULT_THEME_ID,
            ui_font: payload.fontId || DEFAULT_FONT_ID,
            ui_accent: accent,
            ui_sidebar: sidebar,
            ui_theme_json: JSON.stringify({
                themeId: payload.themeId,
                fontId: payload.fontId,
                custom: payload.custom,
                tokens: payload.tokens,
                darkTokens: payload.darkTokens,
                lightTokens: payload.lightTokens,
                fontFamily: payload.fontFamily,
                fontHref: payload.fontHref,
            }),
        }).catch(function () {})
    }, 700)
}

export function prefsFromServerConfig(config) {
    if (!config) return null
    var parsed = null
    if (config.ui_theme_json) {
        try {
            parsed = typeof config.ui_theme_json === 'string'
                ? JSON.parse(config.ui_theme_json)
                : config.ui_theme_json
        } catch (e) {}
    }
    var themeId = config.ui_theme || (parsed && parsed.themeId)
    if (!themeId) return null
    return {
        themeId: themeId,
        fontId: normalizeFontId(config.ui_font || (parsed && parsed.fontId) || DEFAULT_FONT_ID),
        custom: Object.assign({}, DEFAULT_CUSTOM, parsed && parsed.custom || {}, {
            accent: config.ui_accent || (parsed && parsed.custom && parsed.custom.accent) || DEFAULT_CUSTOM.accent,
            sidebar: config.ui_sidebar || (parsed && parsed.custom && parsed.custom.sidebar) || DEFAULT_CUSTOM.sidebar,
        }),
    }
}

export function applyFromServerConfig(config, force) {
    var prefs = prefsFromServerConfig(config)
    if (!prefs) return null
    return applySchoolAppearance(prefs, force)
}

export function setThemeId(themeId) {
    var prefs = readStoredPreference()
    prefs.themeId = themeId
    var payload = applySchoolAppearance(prefs, true)
    persistSchoolTheme(payload)
    return payload
}

export function setFontId(fontId) {
    var prefs = readStoredPreference()
    prefs.fontId = fontId
    var payload = applySchoolAppearance(prefs, true)
    persistSchoolTheme(payload)
    return payload
}

export function setCustomColors(custom) {
    var prefs = readStoredPreference()
    prefs.themeId = 'custom'
    prefs.custom = Object.assign({}, DEFAULT_CUSTOM, custom)
    var payload = applySchoolAppearance(prefs, true)
    persistSchoolTheme(payload)
    return payload
}
